import { styled } from "@linaria/react"
import { Popover, PopoverContent, PopoverPortal } from "@radix-ui/react-popover"
import { observer } from "mobx-react-lite"
import { type PropsWithChildren } from "react"

import { Button } from "@/components/Button/Button.tsx"
import { DropdownMenuStyles } from "@/components/DropdownMenu/DropdownMenuStyles.ts"
import { Horizontal, Vertical } from "@/components/Flex.ts"
import { Heading5 } from "@/components/Heading/Heading.tsx"
import { useInvalidateQueries, useQueries } from "@/hooks/useRootContext.ts"
import { CloseLight, Icon16, LockRegular } from "@/icons/Icons.ts"
import * as textStyles from "@/styles/textStyles.ts"

interface ShareLinkPopupProps extends PropsWithChildren {
  align?: "center" | "start" | "end"
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  onInviteTeammate: () => void
}

export const SHARE_LINK_TOOLTIP_SHOWN_KEY = "share-link-tooltip-shown"

export const ShareLinkPopup = observer((
  {
    align,
    isOpen,
    onOpenChange,
    onInviteTeammate,
    children
  }: ShareLinkPopupProps
) => {
  return (
    <Popover open={isOpen}>
      {children}
      <PopoverPortal>
        <StyledPopoverContent
          className={DropdownMenuStyles.Content}
          align={align}
          side="bottom"
          sideOffset={8}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onPointerDownOutside={() => onOpenChange(false)}
        >
          <ShareLinkPopupContent onOpenChange={onOpenChange} onInviteTeammate={onInviteTeammate} />
        </StyledPopoverContent>
      </PopoverPortal>
    </Popover>
  )
})

const ShareLinkPopupContent = (props: {
  onOpenChange: (open: boolean, doNotShowAgain?: boolean) => void
  onInviteTeammate: () => void
}) => {
  const client = useQueries().apiClient
  const invalidateQueries = useInvalidateQueries()

  return (
    <>
      <CloseButton kind="icon" aria-label="Close" onClick={() => props.onOpenChange(false, false)}>
        <CloseLight className={Icon16} />
      </CloseButton>

      <Horizontal gap="8px">
        <LockRegular />
        <Vertical gap="16px">
          <Vertical gap="8px">
            <Heading5>Preview is private</Heading5>
            <Description>
              Your project's safety matters to us. To allow others to view and interact with this preview, invite
              them to Matter.
            </Description>
          </Vertical>

          <Vertical alignSelf="flex-start">
            <Horizontal gap="8px" alignItems="center">
              <InviteButton kind="secondary" onClick={() => {
                props.onInviteTeammate()
                props.onOpenChange(false)
              }}>Invite a teammate</InviteButton>

              <DoNotShowAgain href="#" onClick={(e) => {
                e.preventDefault()
                void client.setProfilePreference(SHARE_LINK_TOOLTIP_SHOWN_KEY, "true")
                void invalidateQueries({ queryKey: ["getProfilePreference", SHARE_LINK_TOOLTIP_SHOWN_KEY] })
                props.onOpenChange(false, true)
              }}>
                Do not show again
              </DoNotShowAgain>
            </Horizontal>
          </Vertical>
        </Vertical>
      </Horizontal>
    </>
  )
}


const Description = styled.div`
  ${textStyles.smallestFontSize};

  line-height: 119%;

  color: var(--secondary-text-color);
`

const CloseButton = styled(Button)`
  position: absolute;
  right: 12px;
  top: 12px;
`

const InviteButton = styled(Button)`
  font-weight: 400;
`

const DoNotShowAgain = styled.a`
  ${textStyles.smallFontSize};
  white-space: nowrap;
  color: var(--secondary-text-color);
`

const StyledPopoverContent = styled(PopoverContent)`
  width: 360px;
  padding: 16px;

  border-radius: var(--border-radius-20);
  background-color: var(--text-color-2-opaque);

  z-index: var(--z-index-dropdown);
`